import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, MapPinIcon, UserIcon, AlertTriangleIcon, FileTextIcon, CheckIcon, InfoIcon } from 'lucide-react';
import { dataService } from '../../services/DataService';
import { TrainingService, ServiceStatus } from '../../models/TrainingService';
import { Project } from '../../models/Project';
import { User } from '../../models/User';
import { RosterEntry } from '../../models/Roster';
import { useAuth } from '../../contexts/AuthContext';
export function InstructorSchedule() {
  const {
    userId
  } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('week');
  const [trainings, setTrainings] = useState<TrainingService[]>([]);
  const [selectedTraining, setSelectedTraining] = useState<string | null>(null);
  const [trainingDetails, setTrainingDetails] = useState<{
    service?: TrainingService;
    project?: Project;
    instructor?: User;
    rosterEntry?: RosterEntry;
  } | null>(null);
  // Calculate date range based on view mode
  const getDateRange = () => {
    const today = new Date(currentDate);
    if (viewMode === 'day') {
      const start = new Date(today.setHours(0, 0, 0, 0));
      const end = new Date(today.setHours(23, 59, 59, 999));
      return {
        start,
        end
      };
    } else if (viewMode === 'week') {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1)); // Start from Monday
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return {
        start,
        end
      };
    } else {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
      return {
        start,
        end
      };
    }
  };
  // Load trainings based on date range
  useEffect(() => {
    const {
      start,
      end
    } = getDateRange();
    let services = dataService.getTrainingServicesByDateRange(start, end);
    // Filter by current user if in instructor role
    if (userId) {
      services = services.filter(service => service.assignedUserId === userId);
    }
    setTrainings(services);
  }, [currentDate, viewMode, userId]);
  // Load training details when a training is selected
  useEffect(() => {
    if (selectedTraining) {
      const details = dataService.getFullTrainingDetails(selectedTraining);
      setTrainingDetails(details);
    } else {
      setTrainingDetails(null);
    }
  }, [selectedTraining]);
  // Navigate to previous period
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };
  // Navigate to next period
  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };
  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  // Format date for header
  const formatDateHeader = () => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('nl-NL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } else if (viewMode === 'week') {
      const {
        start,
        end
      } = getDateRange();
      return `${start.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long'
      })} - ${end.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })}`;
    } else {
      return currentDate.toLocaleDateString('nl-NL', {
        month: 'long',
        year: 'numeric'
      });
    }
  };
  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('nl-NL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };
  // Get badge color based on training status
  const getStatusBadge = (status: ServiceStatus) => {
    switch (status) {
      case ServiceStatus.CONFIRMED:
        return <Badge variant="success" data-id="element-2757">Bevestigd</Badge>;
      case ServiceStatus.PLANNED:
        return <Badge variant="warning" data-id="element-2758">Gepland</Badge>;
      case ServiceStatus.IN_PROGRESS:
        return <Badge variant="info" data-id="element-2759">In uitvoering</Badge>;
      case ServiceStatus.COMPLETED:
        return <Badge variant="default" data-id="element-2760">Afgerond</Badge>;
      case ServiceStatus.CANCELLED:
        return <Badge variant="danger" data-id="element-2761">Geannuleerd</Badge>;
      default:
        return <Badge variant="default" data-id="element-2762">Onbekend</Badge>;
    }
  };
  // Render day view
  const renderDayView = () => {
    const dayTrainings = trainings.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    return <div className="space-y-4" data-id="element-2763">
        {dayTrainings.length > 0 ? dayTrainings.map(training => <div key={training.id} className={`p-4 border rounded-lg cursor-pointer transition hover:shadow-md ${selectedTraining === training.id ? 'border-[#B61C1C] ring-2 ring-[#B61C1C] bg-red-50' : 'border-gray-200'}`} onClick={() => setSelectedTraining(training.id)} data-id="element-2764">
              <div className="flex justify-between items-start" data-id="element-2765">
                <div data-id="element-2766">
                  <h3 className="font-medium text-gray-900" data-id="element-2767">
                    {training.description}
                  </h3>
                  <div className="flex items-center mt-1 text-sm text-gray-600" data-id="element-2768">
                    <ClockIcon className="h-4 w-4 mr-1" data-id="element-2769" />
                    <span data-id="element-2770">
                      {formatTime(training.startTime)} -{' '}
                      {formatTime(training.endTime)}
                    </span>
                  </div>
                  <div className="flex items-center mt-1 text-sm text-gray-600" data-id="element-2771">
                    <MapPinIcon className="h-4 w-4 mr-1" data-id="element-2772" />
                    <span data-id="element-2773">
                      {dataService.getProjectById(training.projectId)?.location.address}
                      ,{' '}
                      {dataService.getProjectById(training.projectId)?.location.city}
                    </span>
                  </div>
                </div>
                <div data-id="element-2774">
                  {getStatusBadge(training.status)}
                  {training.requiresProtocol && <div className="mt-2 flex items-center" data-id="element-2775">
                      <AlertTriangleIcon className="h-4 w-4 text-yellow-500 mr-1" data-id="element-2776" />
                      <span className="text-xs text-yellow-700" data-id="element-2777">
                        Protocol vereist
                      </span>
                    </div>}
                </div>
              </div>
            </div>) : <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg" data-id="element-2778">
            <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" data-id="element-2779" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-2780">
              Geen trainingen
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-2781">
              Er zijn geen trainingen gepland voor deze dag
            </p>
          </div>}
      </div>;
  };
  // Render week view
  const renderWeekView = () => {
    const {
      start
    } = getDateRange();
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const dayTrainings = trainings.filter(training => {
        const trainingDate = new Date(training.startTime);
        return trainingDate.getDate() === date.getDate() && trainingDate.getMonth() === date.getMonth() && trainingDate.getFullYear() === date.getFullYear();
      }).sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
      const isToday = date.toDateString() === new Date().toDateString();
      days.push(<div key={i} className="min-w-[200px]" data-id="element-2782">
          <div className={`py-2 text-center font-medium border-b ${isToday ? 'bg-[#B61C1C] text-white' : 'bg-gray-50 text-gray-700'}`} data-id="element-2783">
            {date.toLocaleDateString('nl-NL', {
            weekday: 'short',
            day: 'numeric'
          })}
          </div>
          <div className="p-2 space-y-2 h-full min-h-[200px]" data-id="element-2784">
            {dayTrainings.length > 0 ? dayTrainings.map(training => <div key={training.id} className={`p-2 border rounded cursor-pointer transition hover:shadow-sm ${selectedTraining === training.id ? 'border-[#B61C1C] bg-red-50' : 'border-gray-200'}`} onClick={() => setSelectedTraining(training.id)} data-id="element-2785">
                  <div className="text-xs font-medium text-gray-900 truncate" data-id="element-2786">
                    {training.description}
                  </div>
                  <div className="flex items-center mt-1 text-xs text-gray-600" data-id="element-2787">
                    <ClockIcon className="h-3 w-3 mr-1" data-id="element-2788" />
                    <span data-id="element-2789">
                      {formatTime(training.startTime)} -{' '}
                      {formatTime(training.endTime)}
                    </span>
                  </div>
                  <div className="flex items-center mt-1 text-xs text-gray-600" data-id="element-2790">
                    <MapPinIcon className="h-3 w-3 mr-1" data-id="element-2791" />
                    <span className="truncate" data-id="element-2792">
                      {dataService.getProjectById(training.projectId)?.location.city}
                    </span>
                  </div>
                </div>) : <div className="flex items-center justify-center h-full text-gray-400 text-xs" data-id="element-2793">
                Geen trainingen
              </div>}
          </div>
        </div>);
    }
    return <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2794">
        <div className="flex overflow-x-auto" data-id="element-2795">{days}</div>
      </div>;
  };
  // Render month view
  const renderMonthView = () => {
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const weeks = [];
    let days = [];
    // Adjust first day of week (0 = Sunday, 1 = Monday)
    const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    // Fill in days from previous month
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(<div key={`prev-${i}`} className="h-24 bg-gray-50" data-id="element-2796"></div>);
    }
    // Fill in days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayTrainings = trainings.filter(training => {
        const trainingDate = new Date(training.startTime);
        return trainingDate.getDate() === date.getDate() && trainingDate.getMonth() === date.getMonth() && trainingDate.getFullYear() === date.getFullYear();
      });
      const isToday = date.toDateString() === new Date().toDateString();
      days.push(<div key={day} className={`h-24 border border-gray-200 p-1 overflow-y-auto ${isToday ? 'bg-blue-50' : 'bg-white'}`} data-id="element-2797">
          <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : ''}`} data-id="element-2798">
            {day}
          </div>
          <div className="space-y-1 mt-1" data-id="element-2799">
            {dayTrainings.slice(0, 3).map(training => <div key={training.id} className="px-1 py-0.5 rounded text-xs truncate bg-red-100 text-red-800 cursor-pointer" onClick={e => {
            e.stopPropagation();
            setSelectedTraining(training.id);
          }} data-id="element-2800">
                {formatTime(training.startTime)}{' '}
                {training.description.substring(0, 15)}
                {training.description.length > 15 ? '...' : ''}
              </div>)}
            {dayTrainings.length > 3 && <div className="text-xs text-gray-500 text-center" data-id="element-2801">
                +{dayTrainings.length - 3} meer
              </div>}
          </div>
        </div>);
      // Start new row after Saturday (or when we reach the end)
      if ((days.length - adjustedFirstDay) % 7 === 0 || day === daysInMonth) {
        // Fill in remaining days from next month
        if (day === daysInMonth) {
          const remainingDays = 7 - days.length % 7;
          if (remainingDays < 7) {
            for (let i = 0; i < remainingDays; i++) {
              days.push(<div key={`next-${i}`} className="h-24 bg-gray-50" data-id="element-2802"></div>);
            }
          }
        }
        weeks.push(<div key={weeks.length} className="grid grid-cols-7 gap-1" data-id="element-2803">
            {days}
          </div>);
        days = [];
      }
    }
    return <div className="space-y-1" data-id="element-2804">
        <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-2805">
          {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => <div key={day} className="text-center font-medium text-gray-700 py-1" data-id="element-2806">
              {day}
            </div>)}
        </div>
        {weeks}
      </div>;
  };
  return <div className="space-y-6" data-id="element-2807">
      <div className="flex justify-between items-center" data-id="element-2808">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2809">Mijn Rooster</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2810">
        <div className="lg:col-span-2" data-id="element-2811">
          <Card data-id="element-2812">
            <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-2813">
              <div className="flex space-x-2" data-id="element-2814">
                <Button variant="outline" size="sm" onClick={goToPrevious} data-id="element-2815">
                  <ChevronLeftIcon className="h-4 w-4" data-id="element-2816" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday} data-id="element-2817">
                  Vandaag
                </Button>
                <Button variant="outline" size="sm" onClick={goToNext} data-id="element-2818">
                  <ChevronRightIcon className="h-4 w-4" data-id="element-2819" />
                </Button>
              </div>
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2820">
                {formatDateHeader()}
              </h2>
              <div className="flex space-x-2" data-id="element-2821">
                <Button variant={viewMode === 'day' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('day')} data-id="element-2822">
                  Dag
                </Button>
                <Button variant={viewMode === 'week' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('week')} data-id="element-2823">
                  Week
                </Button>
                <Button variant={viewMode === 'month' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('month')} data-id="element-2824">
                  Maand
                </Button>
              </div>
            </div>
            <div className="p-4" data-id="element-2825">
              {viewMode === 'day' && renderDayView()}
              {viewMode === 'week' && renderWeekView()}
              {viewMode === 'month' && renderMonthView()}
            </div>
          </Card>
        </div>
        <div className="lg:col-span-1" data-id="element-2826">
          {trainingDetails?.service ? <Card data-id="element-2827">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2828">
                <h3 className="font-medium text-gray-900" data-id="element-2829">Training Details</h3>
                {getStatusBadge(trainingDetails.service.status)}
              </div>
              <div className="p-4 space-y-4" data-id="element-2830">
                <div data-id="element-2831">
                  <h4 className="text-lg font-medium text-gray-900" data-id="element-2832">
                    {trainingDetails.service.description}
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2833">
                    {trainingDetails.service.trainingType}
                  </p>
                </div>
                <div className="space-y-3" data-id="element-2834">
                  <div className="flex items-start" data-id="element-2835">
                    <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-2836" />
                    <div data-id="element-2837">
                      <p className="text-sm font-medium text-gray-900" data-id="element-2838">
                        Datum & Tijd
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2839">
                        {formatDate(trainingDetails.service.startTime)} |{' '}
                        {formatTime(trainingDetails.service.startTime)} -{' '}
                        {formatTime(trainingDetails.service.endTime)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-2840">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-2841" />
                    <div data-id="element-2842">
                      <p className="text-sm font-medium text-gray-900" data-id="element-2843">
                        Locatie
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2844">
                        {trainingDetails.project?.location.address},{' '}
                        {trainingDetails.project?.location.postalCode}{' '}
                        {trainingDetails.project?.location.city}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-2845">
                    <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-2846" />
                    <div data-id="element-2847">
                      <p className="text-sm font-medium text-gray-900" data-id="element-2848">Klant</p>
                      <p className="text-sm text-gray-600" data-id="element-2849">
                        {trainingDetails.project?.clientName} (Project:{' '}
                        {trainingDetails.project?.projectNumber})
                      </p>
                    </div>
                  </div>
                  {trainingDetails.service.specialNotes && <div className="flex items-start" data-id="element-2850">
                      <AlertTriangleIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" data-id="element-2851" />
                      <div data-id="element-2852">
                        <p className="text-sm font-medium text-gray-900" data-id="element-2853">
                          Bijzonderheden
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-2854">
                          {trainingDetails.service.specialNotes}
                        </p>
                      </div>
                    </div>}
                  {trainingDetails.rosterEntry?.weatherForecast && <div className="flex items-start" data-id="element-2855">
                      <InfoIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5" data-id="element-2856" />
                      <div data-id="element-2857">
                        <p className="text-sm font-medium text-gray-900" data-id="element-2858">
                          Weersverwachting
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-2859">
                          {trainingDetails.rosterEntry.weatherForecast.condition}
                          ,{' '}
                          {trainingDetails.rosterEntry.weatherForecast.temperature}
                          °C, Wind:{' '}
                          {trainingDetails.rosterEntry.weatherForecast.windSpeed}{' '}
                          km/u, Neerslag:{' '}
                          {trainingDetails.rosterEntry.weatherForecast.precipitation}
                          %
                        </p>
                      </div>
                    </div>}
                </div>
                <div className="pt-3 border-t border-gray-200" data-id="element-2860">
                  <div className="flex space-x-2" data-id="element-2861">
                    {trainingDetails.service.status === ServiceStatus.CONFIRMED && <Button variant="primary" size="sm" data-id="element-2862">
                        <CheckIcon className="h-4 w-4 mr-1" data-id="element-2863" />
                        Start Training
                      </Button>}
                    <Button variant="outline" size="sm" data-id="element-2864">
                      <FileTextIcon className="h-4 w-4 mr-1" data-id="element-2865" />
                      Documenten
                    </Button>
                  </div>
                </div>
              </div>
            </Card> : <Card data-id="element-2866">
              <div className="p-6 text-center" data-id="element-2867">
                <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" data-id="element-2868" />
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2869">
                  Geen training geselecteerd
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2870">
                  Selecteer een training om details te bekijken
                </p>
              </div>
            </Card>}
        </div>
      </div>
    </div>;
}